import type {
  DealOffer,
  DealOfferFormatOption,
  DealOfferSummary,
} from "@/components/marketplace/deal-offers-types";
import type { FormatCategoryWithOther } from "@garimpa/lib/formatGroups";

export function requestedDealOfferFormat(format?: FormatCategoryWithOther | "all") {
  return format === "all" ? null : (format ?? null);
}

/**
 * Offer format switch options: only concrete formats that have a current or monitored offer.
 * The aggregate "all" summary is never an option; an "all" page selection resolves to the
 * cheapest offer's format through `preferredDealOfferFormat`.
 */
export function dealOfferFormatOptions(
  summaries: DealOfferSummary[],
  outOfStockOffers: DealOffer[],
): DealOfferFormatOption[] {
  const concreteSummaries = summaries.filter((summary) => summary.formatKey !== "all");
  const availableFormats = new Set(concreteSummaries.map((summary) => summary.formatKey));
  const options: DealOfferFormatOption[] = concreteSummaries.map((summary) => ({
    availability: "available",
    currency: summary.currency,
    format: summary.formatKey as FormatCategoryWithOther,
    offerCount: summary.offerCount,
    price: summary.bestOfferPriceTotal,
  }));
  const outOfStockByFormat = new Map<FormatCategoryWithOther, DealOffer[]>();

  for (const offer of outOfStockOffers) {
    if (!offer.formatCategory || availableFormats.has(offer.formatCategory)) continue;
    const offers = outOfStockByFormat.get(offer.formatCategory) ?? [];
    offers.push(offer);
    outOfStockByFormat.set(offer.formatCategory, offers);
  }

  for (const [format, offers] of outOfStockByFormat) {
    options.push({
      availability: "out_of_stock",
      currency: offers[0]?.priceHistoryCurrency,
      format,
      offerCount: offers.length,
      price: null,
    });
  }

  return options;
}

export function preferredDealOfferFormat(input: {
  options: DealOfferFormatOption[];
  requestedFormat: FormatCategoryWithOther | null;
  summary: DealOfferSummary | null | undefined;
}) {
  if (
    input.requestedFormat &&
    input.options.some((candidate) => candidate.format === input.requestedFormat)
  ) {
    return input.requestedFormat;
  }
  const bestFormat = input.summary?.bestOfferFormatCategory;
  if (bestFormat && input.options.some((candidate) => candidate.format === bestFormat)) {
    return bestFormat;
  }
  return cheapestAvailableFormat(input.options) ?? input.options[0]?.format ?? null;
}

function optionPrice(option: DealOfferFormatOption) {
  if (option.availability !== "available" || option.price == null) return null;
  const price = Number(option.price);
  return Number.isFinite(price) ? price : null;
}

function cheapestAvailableFormat(options: DealOfferFormatOption[]) {
  let cheapest: { format: FormatCategoryWithOther; price: number } | null = null;
  for (const option of options) {
    const price = optionPrice(option);
    if (price === null || (cheapest && cheapest.price <= price)) continue;
    cheapest = { format: option.format, price };
  }
  return cheapest?.format ?? null;
}

export function selectedDealOfferSummary(input: {
  activeFormat: FormatCategoryWithOther | null;
  formatSummaries: DealOfferSummary[];
}) {
  if (!input.activeFormat) return null;
  return (
    input.formatSummaries.find((candidate) => candidate.formatKey === input.activeFormat) ?? null
  );
}

export function selectedOutOfStockDealOffer(input: {
  activeFormat: FormatCategoryWithOther | null;
  offers: DealOffer[];
}) {
  if (!input.activeFormat) return input.offers[0] ?? null;
  return input.offers.find((offer) => offer.formatCategory === input.activeFormat) ?? null;
}
